import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchContestGameLogs } from '@/api/stats';
import { GameLogLeaderboardEntry } from '@/api/schemas';
import { getAbbreviation } from '@/utils/teams';
import Spinner from '@/components/common/Spinner';

// ============================================================================
// Types
// ============================================================================

type PositionFilter = 'FW' | 'MF' | 'DF' | null;

interface Top10TableProps {
  heading: string;                // e.g. "TOP 10 MIDFIELDERS"
  contestId: number;
  position?: PositionFilter;      // null/undefined = overall (no position filter)
  token: string | null;
  onPlayerClick?: (entry: GameLogLeaderboardEntry) => void;
}

// ============================================================================
// Helpers
// ============================================================================

const formatSalary = (n: number | null | undefined): string =>
  n != null ? `$${n.toLocaleString('en-US')}` : '—';

const formatPts = (n: number): string => n.toFixed(1);

/** Compute display rank strings with T- prefix for ties. Assumes rows are pre-sorted by PTS desc. */
const computeRanks = (rows: GameLogLeaderboardEntry[]): string[] => {
  const ranks: string[] = [];
  let i = 0;
  while (i < rows.length) {
    let j = i + 1;
    while (j < rows.length && rows[j].fantasy_points === rows[i].fantasy_points) {
      j++;
    }
    const tieCount = j - i;
    const rankNum = i + 1;
    const prefix = tieCount > 1 ? `T-${rankNum}` : String(rankNum);
    for (let k = i; k < j; k++) ranks.push(prefix);
    i = j;
  }
  return ranks;
};

// Parse "vs BUR" / "@NEW" into parts so we can style the @ prefix.
const parseOpp = (opp: string | null | undefined): { isAway: boolean; suffix: string } => {
  const s = (opp || '').trim();
  if (s.startsWith('@')) return { isAway: true, suffix: s.slice(1).trim() };
  return { isAway: false, suffix: s };
};

// ============================================================================
// Sub-components
// ============================================================================

const RankBadge: React.FC<{ rank: string }> = ({ rank }) => {
  const tied = rank.toUpperCase().startsWith('T');
  const sizing = tied
    ? 'min-w-[38px] sm:min-w-[52px] h-[24px] sm:h-[30px] px-1 sm:px-2 text-badge sm:text-[12px] tracking-[0.5px] sm:tracking-[1px]'
    : 'min-w-[28px] sm:min-w-[40px] h-[24px] sm:h-[30px] px-2 sm:px-3 text-caption sm:text-sm';
  return (
    <span
      className={[
        'inline-flex items-center justify-center rounded-md',
        'bg-accent-yellow text-primary',
        'font-heading font-bold tracking-wide',
        'shadow-[0_2px_8px_rgba(255,196,44,0.25)]',
        sizing,
      ].join(' ')}
    >
      {rank}
    </span>
  );
};

const TeamPosPill: React.FC<{ team: string | null | undefined; position: string | null | undefined }> = ({
  team,
  position,
}) => {
  if (!team && !position) return null;
  return (
    <span className="ml-1.5 sm:ml-2 inline-block align-middle px-1.5 sm:px-2 py-0 sm:py-0.5 rounded bg-gray-200/10 text-gray-300 font-mono font-bold text-micro sm:text-badge tracking-wider whitespace-nowrap">
      {team || '—'}
      {team && position && <span className="text-gray-500"> · </span>}
      {position && <span className="text-accent-yellow">{position}</span>}
    </span>
  );
};

const OppCell: React.FC<{ opp: string | null | undefined }> = ({ opp }) => {
  const { isAway, suffix } = parseOpp(opp);
  if (!opp) return <span className="text-gray-500">—</span>;
  if (!isAway) return <span className="text-gray-200 text-sm">{opp}</span>;
  return (
    <span className="text-gray-200 text-sm">
      <span className="italic text-gray-400 mr-0.5">@</span>
      {suffix}
    </span>
  );
};

const StatCell: React.FC<{ value: number }> = ({ value }) => (
  <td
    className={[
      'px-2 py-3 sm:px-3 sm:py-4 text-center whitespace-nowrap',
      'font-mono font-medium text-sm',
      value === 0 ? 'text-gray-600' : 'text-gray-200',
    ].join(' ')}
  >
    {value}
  </td>
);

// ============================================================================
// Main component
// ============================================================================

const Top10Table: React.FC<Top10TableProps> = ({ heading, contestId, position, token, onPlayerClick }) => {
  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['blog-top10', contestId, position ?? 'overall'],
    queryFn: () =>
      fetchContestGameLogs(
        contestId,
        {
          sort_by: 'fantasy_points',
          order: 'desc',
          limit: 10,
          position: position ?? undefined,
        },
        token
      ),
    enabled: !!contestId,
    refetchOnWindowFocus: false,
  });

  const ranks = useMemo(() => computeRanks(rows), [rows]);

  // Stat column definitions — all visible on every viewport; the table
  // horizontally scrolls on narrow screens so readers can swipe for more stats.
  const statColumns: Array<{ label: string; key: keyof GameLogLeaderboardEntry }> = [
    { label: 'GLs',   key: 'goals' },
    { label: 'SoT',   key: 'shots_on_target' },
    { label: 'Shots', key: 'shots' },
    { label: 'GC',    key: 'gca' },
    { label: 'SC',    key: 'sca' },
    { label: 'IP',    key: 'prog_passes' },
    { label: 'IC',    key: 'prog_carries' },
    { label: 'DIS',   key: 'dispossessed' },
  ];

  return (
    <div className="w-full mb-6">
      <div className="relative overflow-hidden rounded-lg border border-accent-yellow/60 bg-surface-card shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            {/* ===== Header ===== */}
            <thead>
              <tr className="bg-accent-yellow/10 border-b border-accent-yellow/60">
                <th
                  colSpan={2}
                  className="text-left pl-2 sm:pl-6 py-3 sm:py-4 font-heading font-bold text-sm sm:text-lg tracking-[1.5px] sm:tracking-[2px] uppercase text-white whitespace-nowrap sticky left-0 bg-surface-raised z-10"
                >
                  {heading}
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-accent-yellow/10 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                  Pts
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-primary-dark/60 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                  Opp
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-primary-dark/60 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                  Sal
                </th>
                {statColumns.map(({ label }) => (
                  <th
                    key={label}
                    className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-primary-dark/60 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>

            {/* ===== Body ===== */}
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={99} className="py-12 text-center">
                    <Spinner size="md" />
                  </td>
                </tr>
              )}
              {!isLoading && error && (
                <tr>
                  <td colSpan={99} className="py-8 text-center text-secondary italic text-sm">
                    Could not load this table.
                  </td>
                </tr>
              )}
              {!isLoading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan={99} className="py-8 text-center text-gray-500 italic text-sm">
                    No performances yet.
                  </td>
                </tr>
              )}
              {!isLoading && !error && rows.map((row, idx) => {
                const isEven = idx % 2 === 1;
                const isLast = idx === rows.length - 1;
                const isClickable = !!onPlayerClick;
                return (
                  <tr
                    key={`${row.player_id}-${row.match_id}`}
                    className={[
                      isEven ? 'bg-primary-dark/30' : '',
                      isLast ? '' : 'border-b border-gray-700/40',
                      isClickable ? 'cursor-pointer hover:bg-primary/50 transition-colors duration-150 ease-in-out' : '',
                    ].join(' ')}
                    onClick={isClickable ? () => onPlayerClick!(row) : undefined}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={
                      isClickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onPlayerClick!(row);
                            }
                          }
                        : undefined
                    }
                  >
                    {/* Rank */}
                    <td className="pl-2 pr-1 sm:pl-6 sm:pr-2 py-3 sm:py-4 text-left whitespace-nowrap sticky left-0 bg-surface-card z-[5]">
                      <RankBadge rank={ranks[idx] || String(idx + 1)} />
                    </td>

                    {/* Player + pill (team abbreviation keeps mobile width tight) */}
                    <td className="py-3 sm:py-4 pr-2 text-left whitespace-nowrap sticky left-[50px] sm:left-[88px] bg-surface-card z-[5]">
                      <span
                        className={[
                          'font-body font-bold text-[13px] sm:text-base text-white tracking-tight',
                          isClickable ? 'hover:text-accent-yellow hover:underline underline-offset-2' : '',
                        ].join(' ')}
                      >
                        {row.player_name}
                      </span>
                      <TeamPosPill
                        team={getAbbreviation(row.current_team_name) || row.current_team_name}
                        position={row.primary_position}
                      />
                    </td>

                    {/* PTS — highlighted, right after Player so it's the first scrollable column on mobile */}
                    <td className="px-2 sm:px-3 py-3 sm:py-4 text-center whitespace-nowrap font-heading font-bold text-lg sm:text-xl tracking-wider text-accent-yellow bg-accent-yellow/5">
                      {formatPts(row.fantasy_points)}
                    </td>

                    {/* Opp */}
                    <td className="px-2 sm:px-3 py-3 sm:py-4 text-center whitespace-nowrap">
                      <OppCell opp={row.opponent} />
                    </td>

                    {/* Salary */}
                    <td className="px-2 sm:px-3 py-3 sm:py-4 text-center whitespace-nowrap font-mono font-semibold text-sm text-white">
                      {formatSalary(row.dfs_salary)}
                    </td>

                    {/* Stats */}
                    {statColumns.map(({ key }) => (
                      <StatCell
                        key={key as string}
                        value={Number(row[key]) || 0}
                      />
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Top10Table;
